import React, { useState, useEffect, useRef, useCallback } from 'react';

// SimonButton component for each colored button
function SimonButton({ color, onButtonClick, isHighlighted, disabled }) {
  return (
    <button
      className={`
        w-36 h-36 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56
        rounded-xl shadow-lg transform transition-all duration-100 ease-in-out
        ${color === 'green' ? 'bg-green-500 hover:bg-green-600 active:bg-green-700' : ''}
        ${color === 'red' ? 'bg-red-500 hover:bg-red-600 active:bg-red-700' : ''}
        ${color === 'yellow' ? 'bg-yellow-500 hover:bg-yellow-600 active:bg-yellow-700' : ''}
        ${color === 'blue' ? 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700' : ''}
        ${isHighlighted ? 'brightness-125 scale-105 shadow-2xl ring-4 ring-white ring-opacity-75' : ''}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
      onClick={() => onButtonClick(color)}
      disabled={disabled}
      style={{
        boxShadow: isHighlighted ? '0 0 30px rgba(255, 255, 255, 0.8), inset 0 0 20px rgba(255, 255, 255, 0.5)' : '',
      }}
    >
      {/* Optional: Add a label or icon if desired */}
    </button>
  );
}

// Main App component for the Simon Says game
function SimonGame() {
  const colors = ['green', 'red', 'yellow', 'blue'];
  const [sequence, setSequence] = useState([]);
  const [playerInput, setPlayerInput] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayersTurn, setIsPlayersTurn] = useState(false);
  const [currentRound, setCurrentRound] = useState(0);
  const [gameOver, setGameOver] = useState(true); // Game starts in a non-playing state
  const [statusMessage, setStatusMessage] = useState('Press Start to Play!');
  const [highlightedButton, setHighlightedButton] = useState(null);
const [scoe, setScore] = useState(0); // Score based on rounds completed
  const sequenceDelay = 600; // Delay between each light in the sequence
  const highlightDuration = 300; // How long a button stays highlighted

  const audioRefs = useRef({});
  useEffect(() => {
    colors.forEach(color => {
      audioRefs.current[color] = new Audio(`https://s3.amazonaws.com/freecodecamp/simonSound${colors.indexOf(color) + 1}.mp3`);
    });
  }, []);

  const playSound = useCallback((color) => {
    if (audioRefs.current[color]) {
      audioRefs.current[color].currentTime = 0; // Rewind to start
      audioRefs.current[color].play();
    }
  }, [colors]); // Depend on colors array, though it's constant.

  // Function to start the game
  const startGame = useCallback(() => {
    setSequence([]);
    setPlayerInput([]);
    setCurrentRound(1);
    setScore(0); // Assuming score is rounds completed - 1
    setIsPlaying(true);
    setGameOver(false);
    setStatusMessage('Watch the sequence!');
    setTimeout(() => generateNextSequence(1), 1000); // Start with round 1
  }, []);

  // Function to generate the next sequence
  const generateNextSequence = useCallback((round) => {
    setIsPlayersTurn(false);
    setStatusMessage('Watch the sequence!');
    const newSequence = [...sequence];
    newSequence.push(colors[Math.floor(Math.random() * colors.length)]);
    setSequence(newSequence);
    setPlayerInput([]); // Reset player input for the new round

    // Play the sequence
    let i = 0;
    const playInterval = setInterval(() => {
      if (i < newSequence.length) {
        setHighlightedButton(newSequence[i]);
        playSound(newSequence[i]);
        setTimeout(() => setHighlightedButton(null), highlightDuration);
        i++;
      } else {
        clearInterval(playInterval);
        setTimeout(() => {
          setIsPlayersTurn(true);
          setStatusMessage('Your turn!');
        }, highlightDuration + 200); // Small delay before player's turn
      }
    }, sequenceDelay);
  }, [sequence, colors, playSound, highlightDuration, sequenceDelay]);

  // Handle player's button click
  const handleButtonClick = useCallback((color) => {
    if (!isPlayersTurn || !isPlaying || gameOver) return;

    setPlayerInput(prevInput => {
      const newInput = [...prevInput, color];
      playSound(color); // Play sound immediately on player click

      // Check if the current input matches the sequence
      const currentSequenceIndex = newInput.length - 1;
      if (newInput[currentSequenceIndex] !== sequence[currentSequenceIndex]) {
        // Game Over!
        setGameOver(true);
        setIsPlaying(false);
        setIsPlayersTurn(false);
        setStatusMessage(`Game Over! You reached Round ${currentRound - 1}.`);
        return newInput; // Keep input for potential debug if needed
      }

      // If all sequence matched, go to next round
      if (newInput.length === sequence.length) {
        setCurrentRound(prevRound => prevRound + 1);
        setStatusMessage('Correct! Next Round...');
        setScore(currentRound); // Update score after completing a round
        setTimeout(() => generateNextSequence(currentRound + 1), 1000); // Delay before next sequence
        return []; // Clear input for next round
      }

      return newInput;
    });
  }, [isPlayersTurn, isPlaying, gameOver, sequence, currentRound, playSound, generateNextSequence]);

  // Score display (based on rounds completed)
  const score = currentRound > 0 ? currentRound - 1 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-200 via-indigo-300 to-blue-200 flex items-center justify-center p-4 overflow-hidden">
      <div className="bg-white bg-opacity-90 p-6 sm:p-8 md:p-10 rounded-3xl shadow-2xl transform hover:scale-[1.01] transition-transform duration-300 ease-in-out flex flex-col items-center border border-gray-100 backdrop-blur-sm">
        <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-gray-800 mb-6 text-center tracking-tight leading-tight drop-shadow-md">
          Simon Says
        </h1>

        <div className="mb-6 flex space-x-8 text-xl sm:text-2xl font-bold text-gray-700">
          <div className="flex items-center space-x-2">
            <span className="text-purple-600">Round:</span>
            <span className="text-3xl font-extrabold text-purple-700">{currentRound}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-teal-600">Score:</span>
            <span className="text-3xl font-extrabold text-teal-700">{score}</span>
          </div>
        </div>

        <div className="text-2xl sm:text-3xl font-bold mb-6 text-gray-700 animate-fade-in-up">
          {statusMessage}
        </div>

        <div className="grid grid-cols-2 gap-4 sm:gap-5 md:gap-6 lg:gap-7 p-2 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl shadow-inner">
          {colors.map((color) => (
            <SimonButton
              key={color}
              color={color}
              onButtonClick={handleButtonClick}
              isHighlighted={highlightedButton === color}
              disabled={!isPlayersTurn || gameOver || !isPlaying} // Disable during AI turn or game over
            />
          ))}
        </div>

        <button
          onClick={startGame}
          disabled={isPlaying && !gameOver}
          className={`
            mt-8 px-8 py-4 bg-gradient-to-r from-purple-500 to-indigo-500 text-white font-bold text-xl rounded-full shadow-lg transform transition-all duration-300
            hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-purple-300
            ${isPlaying && !gameOver ? 'opacity-50 cursor-not-allowed' : 'animate-bounce-once'}
          `}
        >
          {isPlaying && !gameOver ? 'Playing...' : 'Start Game'}
        </button>

        {/* Confetti effect on Game Over if score is high (example threshold) */}
        {gameOver && score >= 5 && ( // Changed from timeLeft === 0 to gameOver
          <>
            {Array.from({ length: 70 }).map((_, i) => (
              <div
                key={i}
                className="confetti-piece"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  backgroundColor: `hsl(${Math.random() * 360}, 70%, 60%)`,
                  animationDelay: `${Math.random() * 2}s`,
                  transform: `scale(${0.5 + Math.random()}) rotate(${Math.random() * 360}deg)`,
                  width: `${8 + Math.random() * 12}px`,
                  height: `${8 + Math.random() * 12}px`,
                }}
              ></div>
            ))}
          </>
        )}
      </div>

      <style jsx>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700;800;900&display=swap');
        body {
          font-family: 'Inter', sans-serif;
        }

        .animate-fade-in-up {
            animation: fadeInScaleUp 0.5s ease-out forwards;
        }

        @keyframes fadeInScaleUp {
            from {
                opacity: 0;
                transform: translateY(20px) scale(0.9);
            }
            to {
                opacity: 1;
                transform: translateY(0) scale(1);
            }
        }

        .animate-bounce-once {
            animation: bounceOnce 1s ease-in-out;
        }

        @keyframes bounceOnce {
            0%, 100% {
                transform: translateY(0);
            }
            20% {
                transform: translateY(-10px);
            }
            40% {
                transform: translateY(0);
            }
            60% {
                transform: translateY(-5px);
            }
            80% {
                transform: translateY(0);
            }
        }

        .confetti-piece {
          position: absolute;
          border-radius: 50%;
          opacity: 0;
          animation: fall 3s ease-out forwards;
          z-index: 10;
        }

        @keyframes fall {
          0% {
            opacity: 0;
            transform: translateY(-100px) rotateZ(0deg) scale(0.5);
          }
          10% {
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh + 200px)) rotateZ(720deg) scale(1);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
}

// Make sure to load Tailwind CSS in your public/index.html or main HTML file
// <script src="https://cdn.tailwindcss.com"></script>
export default SimonGame;
